import React, {
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Checkbox,
  Chip,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  MenuList,
  TextField,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { Menu } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';

import clsx from 'clsx';
import { ReactComponent } from 'tss-react/tools/ReactComponent';

import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrowDownIcon.svg';
import { useRegisterDismissLayer } from '../../../context/BackLayerStackContext';
import { getMobileBottomSheetPaperRow } from '../../../styles/mobile-bottom-sheet-paper';
import CloseActionButton from '../ActionButtons/CloseActionButton';
import { useStyles } from './AppChipSelector.styles';

const containsText = (text: string, searchText: string) =>
  text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

/** MUI Menu: mousedown can swallow click on controls inside MenuList. */
function MenuCloseActionButton({
  onClose,
}: {
  onClose: (e: SyntheticEvent) => void;
}) {
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex' }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}>
      <CloseActionButton onClick={onClose} />
    </Box>
  );
}

export interface IAppChipSelector {
  values?: any[];
  onChange: (values: any[]) => void;
  options?: Array<any>;
  disabled?: boolean;
  fieldName?: string;
  className?: string;
  maxTags?: number;
  disabledValues?: Array<any>;
  AvatarComponent?: ReactComponent<any>;
  itemWrapperCallback?: (
    ...props: any[]
  ) => ReactComponent<any> | React.ReactElement;
  label?: string;
  disableSearch?: boolean;
  helperText?: React.ReactNode;
  error?: boolean;
}

const AppChipSelector: React.FC<IAppChipSelector> = ({
  disabled = false,
  values = [],
  disabledValues = [],
  options = [],
  onChange,
  maxTags,
  fieldName = 'name',
  className,
  AvatarComponent,
  itemWrapperCallback,
  label,
  disableSearch = false,
  helperText,
  error = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { classes } = useStyles(theme);
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

  const currentSelectionCount = values?.length || 0;
  const isLimitReached =
    maxTags !== undefined && currentSelectionCount >= maxTags;

  const handleOpenMenu = () => {
    if (disabled) return;
    if (containerRef.current) {
      setMenuWidth(containerRef.current.offsetWidth);
    }
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchText('');
  }, []);

  useRegisterDismissLayer(open, handleClose);

  const filteredOptions = useMemo(() => {
    if (!searchText) return options;
    return options.filter((option) =>
      containsText(option[fieldName], searchText),
    );
  }, [options, searchText, fieldName]);

  const isSelected = (option: any) => {
    return values?.some((v: any) => v.id === option.id) || false;
  };

  const handleToggleOption = (option: any) => {
    if (disabled) return;

    const isCurrentlySelected = isSelected(option);
    let newValues: any[];

    if (isCurrentlySelected) {
      newValues = values.filter((v: any) => v.id !== option.id);
    } else {
      if (isLimitReached) return;
      newValues = [...values, option];
    }

    if (disabledValues.length > 0) {
      const allValues = [
        ...disabledValues,
        ...newValues.filter(
          (v: any) => !disabledValues.some((dv: any) => dv.id === v.id),
        ),
      ];
      onChange(allValues);
    } else {
      onChange(newValues);
    }
  };

  const handleDeleteChip = (option: any, event?: React.MouseEvent) => {
    if (disabled) return;
    if (event) {
      event.stopPropagation();
    }
    const newValues = values.filter((v: any) => v.id !== option.id);

    if (disabledValues.length > 0) {
      const allValues = [
        ...disabledValues,
        ...newValues.filter(
          (v: any) => !disabledValues.some((dv: any) => dv.id === v.id),
        ),
      ];
      onChange(allValues);
    } else {
      onChange(newValues);
    }
  };

  const isOptionDisabled = (option: any) => {
    if (disabled) return true;
    if (isLimitReached && !isSelected(option)) return true;
    return option.disabled || false;
  };

  return (
    <Box className={clsx(classes.root, className)}>
      {label && (
        <Box className={classes.labelContainer}>
          <FormLabel className={classes.label} component="label">
            {label}
          </FormLabel>
        </Box>
      )}
      <Box
        ref={containerRef}
        className={clsx(
          classes.chipsContainer,
          !disabled && classes.chipsContainerClickable,
        )}
        onClick={handleOpenMenu}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={label || 'select options'}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            handleOpenMenu();
          }
        }}>
        <Box className={classes.chipsContent}>
          {values.map((option: any) => {
            const isDisabledValue = disabledValues.some(
              (dv: any) => dv.id === option.id,
            );

            return (
              <Chip
                key={option.id}
                label={option[fieldName]}
                className={clsx(
                  classes.chip,
                  !isDisabledValue && classes.chipWithDelete,
                  !!AvatarComponent && classes.chipWithAvatar,
                  !disabled && classes.chipClickable,
                )}
                avatar={
                  AvatarComponent ? <AvatarComponent tag={option} /> : null
                }
                onDelete={
                  isDisabledValue
                    ? undefined
                    : (event) => handleDeleteChip(option, event)
                }
                deleteIcon={
                  !isDisabledValue ? (
                    <IconButton onClick={(e) => e.stopPropagation()}>
                      <CancelIcon />
                    </IconButton>
                  ) : undefined
                }
                disabled={disabled || isDisabledValue}
              />
            );
          })}
        </Box>
        <Box
          className={clsx(classes.arrowIcon, open && classes.arrowIconOpen)}
          component={ArrowDownIcon}
        />
      </Box>

      {helperText != null && helperText !== '' && (
        <FormHelperText error={error} className={classes.helperText}>
          {helperText}
        </FormHelperText>
      )}

      <Menu
        anchorEl={containerRef.current}
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          className: classes.menuPaper,
          sx: {
            width: menuWidth ? `${menuWidth}px` : 'auto',
            minWidth: menuWidth ? `${menuWidth}px` : 'auto',
            [theme.breakpoints.down('md')]: {
              ...getMobileBottomSheetPaperRow(),
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}>
        <MenuList className={classes.menuList}>
          {disableSearch ? (
            <ListSubheader
              sx={{
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'flex-end',
                alignItems: 'center',
                lineHeight: 1,
                py: 0.5,
              }}>
              <MenuCloseActionButton
                onClose={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleClose();
                }}
              />
            </ListSubheader>
          ) : (
            <>
              <ListSubheader
                sx={{
                  display: { xs: 'block', md: 'none' },
                  lineHeight: 1,
                  p: theme.spacing(1),
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                  }}>
                  <TextField
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    className={classes.searchField}
                    autoFocus={isMobile}
                    variant="outlined"
                    placeholder={t('common.typeToSearch')}
                    value={searchText}
                    sx={{ flex: 1, minWidth: 0 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Escape') {
                        e.stopPropagation();
                      }
                    }}
                  />
                  <MenuCloseActionButton
                    onClose={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleClose();
                    }}
                  />
                </Box>
              </ListSubheader>
              <ListSubheader
                sx={{
                  display: { xs: 'none', md: 'block' },
                  p: theme.spacing(1),
                }}>
                <TextField
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  className={classes.searchField}
                  autoFocus={!isMobile}
                  variant="outlined"
                  placeholder={t('common.typeToSearch')}
                  fullWidth
                  value={searchText}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
            </>
          )}
          {filteredOptions.map((option: any) => {
            const selected = isSelected(option);
            const optionDisabled = isOptionDisabled(option);

            return (
              <MenuItem
                key={option.id}
                onClick={() => handleToggleOption(option)}
                disabled={optionDisabled}
                className={classes.menuItem}>
                <Checkbox
                  color="primary"
                  className={classes.checkBox}
                  checked={selected}
                  disabled={optionDisabled}
                />
                {itemWrapperCallback
                  ? itemWrapperCallback(option)
                  : option[fieldName]}
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default AppChipSelector;
